import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, LinearProgress,
  CircularProgress, Stack, Chip, IconButton, Alert, Tooltip, FormControlLabel, Switch,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import apiClient from '../services/apiClient';
import { useLanguage } from '../context/LanguageContext';

/**
 * VocabularyReviewPage — spaced-repetition flashcard review (#4).
 *
 * Fetches the due queue (GET /vocabulary/review), shows each word, lets the user
 * reveal the answer, then grade it (Again/Hard/Good/Easy). Each grade is sent to
 * POST /vocabulary/:id/review which reschedules the word via SM-2-lite.
 *
 * Keyboard: Space/Enter reveals; 1-4 grade once revealed; P plays the word.
 *
 * Audio: each word's pronunciation is fetched once (POST /vocabulary/:id/audio,
 * cached server-side) and auto-plays when the card appears unless the learner
 * turns Auto-play off (remembered in this browser). The next card's audio is
 * prefetched. One <audio> element is unlocked on the first tap for iOS.
 */

const AUTOPLAY_KEY = 'magellan.review.autoplay';
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';

function readAutoplay() {
  try { return window.localStorage.getItem(AUTOPLAY_KEY) !== 'off'; } catch (e) { return true; }
}

const GRADES = [
  { grade: 0, label: 'Again', color: 'error' },
  { grade: 1, label: 'Hard', color: 'warning' },
  { grade: 2, label: 'Good', color: 'primary' },
  { grade: 3, label: 'Easy', color: 'success' },
];

export default function VocabularyReviewPage() {
  const navigate = useNavigate();
  const { selectedLanguage } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [queue, setQueue] = useState([]);
  const [dueCount, setDueCount] = useState(0);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reviewedCount, setReviewedCount] = useState(0);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    setIndex(0);
    setRevealed(false);
    setReviewedCount(0);
    try {
      const res = await apiClient.vocabulary.getReviewQueue(selectedLanguage, 20);
      setQueue(res.data.vocabulary || []);
      setDueCount(res.data.due_count || 0);
    } catch (e) {
      console.error('Failed to load review queue', e);
      setError('Could not load your review queue right now.');
    } finally {
      setLoading(false);
    }
  }, [selectedLanguage]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const current = queue[index];
  const finished = !loading && !error && queue.length > 0 && index >= queue.length;

  // ---- Pronunciation audio ----
  const [autoPlay, setAutoPlay] = useState(readAutoplay);
  const [audioState, setAudioState] = useState('idle'); // idle | loading | playing
  const audioRef = useRef(null);
  const unlockedRef = useRef(false);
  const urlCache = useRef(new Map());   // vocab id -> audio URL
  const currentIdRef = useRef(null);
  currentIdRef.current = current?.id ?? null;

  const unlockAudio = useCallback(() => {
    if (unlockedRef.current) return;
    unlockedRef.current = true;
    try {
      if (!audioRef.current) audioRef.current = new Audio();
      const a = audioRef.current;
      a.muted = true;
      a.src = SILENT_WAV;
      const p = a.play();
      if (p && p.catch) p.catch(() => {});
    } catch (e) { /* audio unavailable */ }
  }, []);

  const getAudioUrl = useCallback(async (item) => {
    if (!item) return null;
    if (urlCache.current.has(item.id)) return urlCache.current.get(item.id);
    const res = await apiClient.vocabulary.getWordAudio(item.id);
    const url = apiClient.audio.getAudioUrl(res.data.job_id);
    urlCache.current.set(item.id, url);
    return url;
  }, []);

  const playWord = useCallback(async (item) => {
    if (!item) return;
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    setAudioState('loading');
    try {
      const url = await getAudioUrl(item);
      if (currentIdRef.current !== item.id) return; // moved on while loading
      a.muted = false;
      a.onended = () => setAudioState('idle');
      a.onerror = () => setAudioState('idle');
      a.src = url;
      const p = a.play();
      setAudioState('playing');
      if (p && p.catch) p.catch(() => setAudioState('idle')); // autoplay blocked: tap to play
    } catch (e) {
      console.error('Word audio failed', e);
      setAudioState('idle');
    }
  }, [getAudioUrl]);

  const toggleAutoPlay = (on) => {
    setAutoPlay(on);
    try { window.localStorage.setItem(AUTOPLAY_KEY, on ? 'on' : 'off'); } catch (e) { /* ignore */ }
  };

  // Auto-play each new card; prefetch the next card's audio.
  useEffect(() => {
    if (!current) return;
    if (autoPlay) playWord(current);
    const next = queue[index + 1];
    if (next) getAudioUrl(next).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const handleGrade = useCallback(async (grade) => {
    if (!current || submitting) return;
    unlockAudio();
    setSubmitting(true);
    try {
      await apiClient.vocabulary.submitReview(current.id, grade);
    } catch (e) {
      console.error('Failed to submit review', e);
      // Non-fatal: keep moving so a transient error doesn't stall the session.
    } finally {
      setSubmitting(false);
      setReviewedCount((n) => n + 1);
      setRevealed(false);
      setIndex((i) => i + 1);
    }
  }, [current, submitting, unlockAudio]);

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e) => {
      if (loading || finished || !current) return;
      if (e.key === 'p' || e.key === 'P') {
        unlockAudio();
        playWord(current);
      } else if (!revealed && (e.code === 'Space' || e.code === 'Enter')) {
        e.preventDefault();
        unlockAudio();
        setRevealed(true);
      } else if (revealed && ['1', '2', '3', '4'].includes(e.key)) {
        handleGrade(Number(e.key) - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [loading, finished, current, revealed, handleGrade, unlockAudio, playWord]);

  const goBack = () => navigate('/vocabulary');

  // ---- Render states ----
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const Header = ({ children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', p: 2, gap: 1 }}>
      <IconButton onClick={goBack} aria-label="Back to vocabulary"><ArrowBackIcon /></IconButton>
      <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>Review</Typography>
      {children}
    </Box>
  );

  if (error) {
    return (
      <Box>
        <Header />
        <Box sx={{ p: 2 }}>
          <Alert severity="error" action={<Button onClick={fetchQueue}>Retry</Button>}>{error}</Alert>
        </Box>
      </Box>
    );
  }

  if (queue.length === 0) {
    return (
      <Box>
        <Header />
        <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
          <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>You're all caught up</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            No words are due for review right now. Add words or come back later.
          </Typography>
          <Button variant="contained" onClick={goBack} sx={{ mt: 3, textTransform: 'none' }}>
            Back to Vocabulary
          </Button>
        </Box>
      </Box>
    );
  }

  if (finished) {
    return (
      <Box>
        <Header />
        <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
          <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Review complete</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            You reviewed {reviewedCount} {reviewedCount === 1 ? 'word' : 'words'}.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 4 }}>
            <Button variant="outlined" onClick={goBack} sx={{ textTransform: 'none' }}>
              Back to Vocabulary
            </Button>
            <Button
              variant="contained"
              startIcon={<ReplayIcon />}
              onClick={fetchQueue}
              sx={{ textTransform: 'none' }}
            >
              Review more
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  const progressPct = Math.round((index / queue.length) * 100);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header>
        <FormControlLabel
          control={<Switch size="small" checked={autoPlay} onChange={(e) => toggleAutoPlay(e.target.checked)} />}
          label={<Typography variant="caption">Auto-play</Typography>}
          sx={{ mr: 1 }}
        />
        <Chip size="small" label={`${index + 1} / ${queue.length}`} />
      </Header>

      <Box sx={{ px: 2 }}>
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
        />
        <Typography variant="caption" color="text.secondary">
          {dueCount} due today
        </Typography>
      </Box>

      <Box sx={{ p: 2, maxWidth: 560, mx: 'auto' }}>
        <Card
          elevation={0}
          sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 3,
            minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <CardContent sx={{ textAlign: 'center', width: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {current.word}
              </Typography>
              <Tooltip title="Hear it (P)">
                <span>
                  <IconButton
                    aria-label="Play pronunciation"
                    color={audioState === 'playing' ? 'primary' : 'default'}
                    disabled={audioState === 'loading'}
                    onClick={() => { unlockAudio(); playWord(current); }}
                  >
                    {audioState === 'loading' ? <CircularProgress size={22} /> : <VolumeUpIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            {revealed && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                  {current.translation || '—'}
                </Typography>
                {current.context && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1.5, fontStyle: 'italic' }}
                  >
                    {current.context}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {!revealed ? (
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => { unlockAudio(); setRevealed(true); }}
            sx={{ mt: 3, py: 1.25, textTransform: 'none', fontWeight: 600 }}
          >
            Show answer
          </Button>
        ) : (
          <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
            {GRADES.map((g) => (
              <Button
                key={g.grade}
                fullWidth
                variant="contained"
                color={g.color}
                disabled={submitting}
                onClick={() => handleGrade(g.grade)}
                sx={{ py: 1.25, textTransform: 'none', fontWeight: 600 }}
              >
                {g.label}
              </Button>
            ))}
          </Stack>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 2 }}
        >
          {revealed ? 'Press 1–4 to grade · P to hear it' : 'Press Space to reveal · P to hear it'}
        </Typography>
      </Box>
    </Box>
  );
}
